import React from 'react';
import Link from 'next/link';
import NextSteps from 'components/NextSteps';
import SalesCalloutSwatches from 'components/SalesCalloutSwatches';
import SalesCalloutHTO from 'components/SalesCalloutHTO';
import Layout1x1 from 'components/Layout1x1';
import { withPageStaticProps } from 'utils/header';
import DoubleHeader from 'components/typography/DoubleHeader';

class CCPA extends React.Component {
  render() {
    return (
      <>
        <Layout1x1 className="m-section-hero">
          <DoubleHeader hero smallTitle={<h2>Revised: September 22, 2025</h2>} largeTitle={<h1>Your California Privacy Rights</h1>} />
          <p>
            If you are a California resident, you have certain rights under the California Consumer Privacy Act (CCPA), as amended by the
            California Privacy Rights Act (CPRA). This section describes those rights and explains how Generation Tux collects, uses, and
            discloses your personal information. If there is any conflict between this section and other parts of our Privacy Policy, this
            section will control for California residents. If you have questions, please contact us at:
          </p>

          <ul className="list-disc-sm my-24">
            <li>Phone: 1-844-726-4889</li>
            <li>
              Email:{' '}
              <Link href="mailto:&#099;&#099;&#112;&#097;&#064;&#103;&#101;&#110;&#101;&#114;&#097;&#116;&#105;&#111;&#110;&#116;&#117;&#120;&#046;&#099;&#111;&#109;">
                ccpa@generationtux.com
              </Link>
            </li>
          </ul>

          <h2 className="text-h2-display">Categories of Personal Information We Collect</h2>

          <p>
            In the past twelve (12) months, we may have collected the following categories of personal information about consumers:
            <ol className="my-24">
              <li>
                <b>Identifiers</b> – such as name, alias, postal address, email address, phone number, account name, IP address, and other
                unique personal identifiers.
              </li>
              <li>
                <b>Customer Records Information (as defined in Cal. Civ. Code § 1798.80(e))</b> – such as signature, billing information,
                and limited payment details processed securely by our third-party payment processors.
              </li>
              <li>
                <b>Protected Classification Characteristics</b> – such as age or date of birth (to confirm eligibility), gender (for fit and
                styling purposes).
              </li>
              <li>
                <b>Commercial Information</b> – such as records of products rented or purchased, order history, and related purchasing
                tendencies.
              </li>
              <li>
                <b>Internet or Network Activity Information</b> – such as browsing history, interactions with our website, or
                advertisements.
              </li>
              <li>
                <b>Geolocation Data</b> – such as general location inferred from your device or shipping address.
              </li>
              <li>
                <b>Professional or Employment Information</b> – if provided to us in connection with special group or corporate events.
              </li>
              <li>
                <b>Inferences</b> – such as preferences or characteristics we derive from the information above to provide personalized
                recommendations.
              </li>
            </ol>
            <b>We do not knowingly collect</b> sensitive personal information such as Social Security numbers, driver’s license numbers,
            biometric data, precise geolocation, or health information.
          </p>

          <h2 className="text-h2-display">Sources of Personal Information</h2>
          <p>
            We collect personal information directly from you (such as when you create an account, place an order, or contact customer
            service), automatically when you interact with our website, and from service providers that help us process transactions,
            provide analytics, or deliver services.
          </p>

          <h2 className="text-h2-display">Purposes for Collecting and Using Personal Information</h2>

          <p>
            We collect and use personal information for the following business purposes:
            <ul className="list-disc-sm my-24">
              <li>To provide, personalize, and improve our products and services.</li>
              <li>To process payments and fulfill orders.</li>
              <li>To communicate with you about your orders, account, or inquiries.</li>
              <li>To provide customer support.</li>
              <li>To market and advertise our services.</li>
              <li>To maintain the security of our website and systems.</li>
              <li>To comply with legal obligations.</li>
            </ul>
          </p>

          <h2 className="text-h2-display">Sharing of Personal Information</h2>

          <p>
            In the past twelve (12) months, we may have disclosed the categories of personal information above with the following categories
            of third parties for business purposes:
            <ul className="list-disc-sm my-24">
              <li>
                <b>Service providers</b> (such as shipping partners, payment processors, IT support, and customer service providers).
              </li>
              <li>
                <b>Marketing and analytics providers</b> (to help us understand usage and improve customer experience).
              </li>
              <li>
                <b>Legal authorities</b> (if required by law).
              </li>
            </ul>
            We do not sell personal information, and we do not share personal information for cross-context behavioral advertising as
            defined by the CCPA.
          </p>

          <h2 className="text-h2-display">Retention of Personal Information</h2>

          <p>
            We retain personal information only as long as reasonably necessary to fulfill the purposes described above, comply with legal
            obligations, resolve disputes, and enforce agreements. Specific retention periods may vary depending on the type of information
            and applicable legal requirements (for example, order history may be retained for tax and accounting purposes).
          </p>

          <h2 className="text-h2-display">Your Rights Under the CCPA</h2>

          <p>
            As a California resident, you have the following rights (subject to certain limitations):
            <ol className="list-disc-sm my-24">
              <li>
                <b>Right to Know and Access</b> – You may request details about the personal information we have collected, used, disclosed,
                or retained about you in the past 12 months.
              </li>
              <li>
                <b>Right to Delete</b> – You may request that we delete personal information we have collected from you, subject to
                exceptions.
              </li>
              <li>
                <b>Right to Correct</b> – You may request that we correct inaccurate personal information.
              </li>
              <li>
                <b>Right to Opt-Out</b> of Sale/Sharing – We do not sell or share your personal information.
              </li>
              <li>
                <b>Right to Limit Use of Sensitive Personal Information</b> – We do not use sensitive personal information for purposes
                other than those permitted by law.
              </li>
              <li>
                <b>Right to Non-Discrimination</b> – We will not discriminate against you for exercising your rights.
              </li>
            </ol>
          </p>

          <h2 className="text-h2-display">How to Exercise Your Rights</h2>
          <p>You may submit a request by:</p>
          <p>
            By Telephone: 1-844-726-4889 <br />
            By Email:{' '}
            <Link
              href="mailto:&#099;&#099;&#112;&#097;&#064;&#103;&#101;&#110;&#101;&#114;&#097;&#116;&#105;&#111;&#110;&#116;&#117;&#120;&#046;&#099;&#111;&#109;"
              className="tracker-text-ccpa-mailto-bottom-20191218-165938"
            >
              ccpa@generationtux.com
            </Link>
          </p>

          <p>
            To process your request, we will need to verify your identity. We will respond to verifiable consumer requests within 45 days as
            required by law.
          </p>
        </Layout1x1>
        <NextSteps
          ctaPageName="privacy"
          salesCallout1={<SalesCalloutHTO ctaPageName="privacy" />}
          salesCallout2={<SalesCalloutSwatches ctaPageName="privacy" />}
        />
      </>
    );
  }
}

export const getStaticProps = withPageStaticProps();

export default CCPA;
